import type { CaseReducer, PayloadAction } from '@reduxjs/toolkit';

import type { DashboardTab } from '@/features/dashboard/types';
import { clock } from '@/services/clock';

import type { DashboardEditState, DraftTab, TabDraft } from '../dashboardEdit.types';
import { markDashboardDirty } from '../lib/dashboardEditValidation';
import { validateDashboardName } from '../lib/invariants';

type State = DashboardEditState;

export const dashboardEditInitialState: DashboardEditState = {
  isInitialized: false,
  isDirty: false,
  dashboardName: '',
  initialDashboardName: '',
  draftTabs: [],
  currentTabId: null,
  tabDrafts: {},
  otherDashboardDrafts: {},
  dirtyDashboards: [],
  allDashboardsData: {},
  insertPosition: null
};

const initializeDraft: CaseReducer<
  State,
  PayloadAction<{
    dashboardName: string;
    initialDashboardName: string;
    draftTabs: DraftTab[];
    tabDrafts: Record<string, TabDraft>;
  }>
> = (state, action) => {
  const { dashboardName, initialDashboardName, draftTabs, tabDrafts } = action.payload;

  state.isInitialized = true;
  state.isDirty = false;
  state.dashboardName = dashboardName;
  state.initialDashboardName = initialDashboardName;
  state.draftTabs = draftTabs;
  state.tabDrafts = tabDrafts;
  state.currentTabId = draftTabs[0]?.id ?? null;
  state.otherDashboardDrafts = {};
  state.dirtyDashboards = [];
  state.insertPosition = null;
};

const resetDraft: CaseReducer<State> = () => dashboardEditInitialState;

const resetToEmpty: CaseReducer<State> = state => {
  const mainTabId = crypto.randomUUID();
  state.draftTabs = [
    { id: mainTabId, name: 'Main', sourceName: null, updatedAt: clock.date().toISOString() }
  ];
  state.tabDrafts = { [mainTabId]: { marketTabApps: [], tiles: [] } };
  state.currentTabId = mainTabId;
  state.isDirty = true;
};

const markDirty: CaseReducer<State> = state => {
  state.isDirty = true;
  markDashboardDirty(state, state.dashboardName);
};

const setAllDashboardsData: CaseReducer<
  State,
  PayloadAction<{ data: Record<string, DashboardTab> }>
> = (state, action) => {
  state.allDashboardsData = action.payload.data;
};

const renameDashboard: CaseReducer<State, PayloadAction<{ name: string }>> = (state, action) => {
  validateDashboardName(action.payload.name);
  state.dashboardName = action.payload.name;
  // Bumps the dashboard-level updatedAt rollup (max across tabs) without touching any
  // widget's own timestamp, since renaming is a dashboard-metadata change, not a widget edit.
  const currentTab =
    state.draftTabs?.find(t => t.id === state.currentTabId) ?? state.draftTabs?.[0];
  if (currentTab) currentTab.updatedAt = clock.date().toISOString();
  state.isDirty = true;
};

export const sessionReducers = {
  initializeDraft,
  resetDraft,
  resetToEmpty,
  markDirty,
  setAllDashboardsData,
  renameDashboard
};
